import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { getPayments, refundPayment } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Select from '@/components/common/Select';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import { StatusBadge } from '@/components/common/Badge';
import { formatCurrency, formatDateTime } from '@/utils/formatters';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';
import type { PaymentRecord } from '@/types/payment';

export default function Payments() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  const { hasPermission } = useAuthStore();
  const queryClient = useQueryClient();
  const canRefund = hasPermission('payments', 'refund');

  const { data, isLoading } = useQuery({
    queryKey: ['payments', page, statusFilter],
    queryFn: () => getPayments(page, 20, statusFilter),
  });

  const refundMutation = useMutation({
    mutationFn: () => {
      const amount = refundAmount ? parseInt(refundAmount) * 100 : undefined;
      return refundPayment(selectedPayment!.stripe_payment_intent_id, refundReason, amount);
    },
    onSuccess: () => {
      toast.success('Refund processed successfully');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedPayment(null);
      setRefundReason('');
      setRefundAmount('');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to process refund');
    },
  });

  const columns = useMemo<ColumnDef<PaymentRecord>[]>(
    () => [
      {
        accessorKey: 'stripe_payment_intent_id',
        header: 'Transaction ID',
        cell: ({ row }) => (
          <span className="font-mono text-sm text-gray-500">
            {row.original.stripe_payment_intent_id.slice(-12)}
          </span>
        ),
      },
      {
        accessorKey: 'business',
        header: 'Business',
        cell: ({ row }) => row.original.business?.name || '-',
      },
      {
        accessorKey: 'amount_cents',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="font-medium">{formatCurrency(row.original.amount_cents)}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'refund_amount_cents',
        header: 'Refunded',
        cell: ({ row }) =>
          row.original.refund_amount_cents
            ? formatCurrency(row.original.refund_amount_cents)
            : '-',
      },
      {
        accessorKey: 'created_at',
        header: 'Date',
        cell: ({ row }) => formatDateTime(row.original.created_at),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const payment = row.original;
          const canBeRefunded =
            canRefund &&
            payment.status === 'succeeded' &&
            (!payment.refund_amount_cents || payment.refund_amount_cents < payment.amount_cents);

          if (!canBeRefunded) return null;

          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPayment(payment);
              }}
            >
              Refund
            </Button>
          );
        },
      },
    ],
    [canRefund]
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'succeeded', label: 'Succeeded' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'partially_refunded', label: 'Partial Refund' },
    { value: 'failed', label: 'Failed' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage payment transactions
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardBody padding="sm">
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            className="w-48"
          />
        </CardBody>
      </Card>

      {/* Payments table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.payments || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={data?.total}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Refund Modal */}
      <Modal
        isOpen={!!selectedPayment}
        onClose={() => setSelectedPayment(null)}
        title="Process Refund"
        description={`Refunding payment ${selectedPayment?.stripe_payment_intent_id.slice(-12)}`}
      >
        {selectedPayment && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Original Amount:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(selectedPayment.amount_cents)}
                  </span>
                </div>
                {selectedPayment.refund_amount_cents && (
                  <div>
                    <span className="text-gray-500">Already Refunded:</span>
                    <span className="ml-2 font-medium">
                      {formatCurrency(selectedPayment.refund_amount_cents)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="refund-amount">
                Refund Amount (leave empty for full refund)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  id="refund-amount"
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  placeholder={((selectedPayment.amount_cents - (selectedPayment.refund_amount_cents || 0)) / 100).toFixed(2)}
                  max={(selectedPayment.amount_cents - (selectedPayment.refund_amount_cents || 0)) / 100}
                  min={0}
                  step="0.01"
                  aria-label="Refund amount"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="refund-reason">
                Reason for Refund
              </label>
              <textarea
                id="refund-reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                rows={3}
                placeholder="Enter the reason for this refund..."
                aria-label="Reason for refund"
              />
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setSelectedPayment(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => refundMutation.mutate()}
            loading={refundMutation.isPending}
            disabled={!refundReason.trim()}
          >
            Process Refund
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
